import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPathForTab } from '@/config/routes';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { 
  Music, 
  Image, 
  Mail, 
  Calendar,  
  Layout,
  User,
  Award,
  Tag,
  Share2,
  Megaphone,
  FileArchive,
  Plus,
  Pencil,
  CheckCircle,
  Activity,
  Lock,
  Crown,
  TrendingUp,
  Eye,
  Palette,
  Globe,
  Users,
  Quote,
  Building2,
  UserCog,
  BriefcaseBusiness,
} from 'lucide-react';
import { usePlan } from '@/context/PlanContext';
import { usePreview } from '@/context/PreviewContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface DashboardProps {
  domain: string;
  user: {
    _id: string;
    username: string;
    fullName?: string;
    user_type: string;
    email: string;
    domain: string;
  } | null;
  setTab: (tab: string) => void;
  unopenedLeadsCount: number;
  refreshKey?: number;
}

interface SectionData {
  count: number;
  lastUpdated: string | null;
  isActive?: boolean;
  hasData?: boolean;
  downloadCount?: number;
}

interface DashboardData {
  heroSection: SectionData;
  aboutSection: SectionData;
  showcase: SectionData;
  imageGallery: SectionData;
  websiteLeads: SectionData;
  promoBanner: SectionData;
  toursEvents: SectionData;
  awards: SectionData;
  categories: SectionData;
  infoKit: SectionData;
  platforms: SectionData;
  services: SectionData;
  artistRoster: SectionData;
  testimonials: SectionData;
  team: SectionData;
  clients: SectionData;
  siteType: 'artist' | 'management';
  toursEventsActive: boolean;
  awardsActive: boolean;
}

interface ProfileCompleteness {
  percentage: number;
  missingItems: { label: string; tab: string }[];
  suggestions: string[];
}

interface ActivityItem {
  _id: string;
  type: 'lead' | 'event' | 'content' | 'section' | 'settings';
  title: string;
  description: string;
  timestamp: string;
  icon: 'mail' | 'calendar' | 'music' | 'image' | 'layout' | 'user' | 'award' | 'tag' | 'share' | 'megaphone' | 'activity';
  actor?: {
    name: string;
    role?: string;
    organizationName?: string;
  };
}

interface VisitorTrendsData {
  date: string;
  sessions: number;
  uniqueVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  avgDurationSeconds: number;
  topEvents: { event: string; count: number }[];
}

interface VisitorTrends {
  period: { days: number | 'all' | 'today' | 'range'; from: string | null; to: string | null };
  daily: VisitorTrendsData[];
  comparison: {
    sessions: { current: number; previous: number; change: number | null };
    uniqueVisitors: { current: number; previous: number; change: number | null };
  };
}

const visitorTrendsChartConfig = {
  sessions: {
    label: 'Total Visits',
    color: 'hsl(var(--primary))',
  },
  uniqueVisitors: {
    label: 'Unique Visitors',
    color: '#10b981',
  },
} satisfies ChartConfig;

// Persisted set of expanded dashboard card IDs. Stored as a JSON array in
// localStorage so a user's expand/collapse choices survive reloads.
const DASHBOARD_EXPANDED_KEY = 'artistcms.dashboardCards.expanded';
const readStoredExpanded = (fallback: string[]): string[] => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(DASHBOARD_EXPANDED_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : fallback;
  } catch {
    return fallback;
  }
};

// Pure formatter — hoisted to module scope so it never becomes a fresh
// reference on Dashboard re-renders (which would in turn force any component
// that closes over it to remount and lose Radix's animation state).
const formatTimeAgo = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatActivityTimestamp = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// SectionCard — renders as an `AccordionItem` so it can collapse to just the
// icon + title + chevron header and expand to reveal the count, "last
// updated" line, and primary CTA.
//
// IMPORTANT: Declared at module scope (NOT inside Dashboard). If this were
// re-declared on every parent render, React would treat each render as a new
// component type and remount every `AccordionItem`, wiping Radix's
// `data-state` transition and breaking the expand/collapse animation.
//
// The collapsed-only count Badge is hidden via CSS (`group-data-[state=open]`)
// rather than a JS `isExpanded` check so we don't need to thread expand state
// as a prop — Radix already exposes it on the item root.
interface SectionCardProps {
  id: string;
  title: string;
  count: number | string;
  lastUpdated: string | null;
  icon: any;
  onClick: () => void;
  ctaLabel?: string;
  emptyCtaLabel?: string;
  isConfigured?: boolean;
  highlight?: boolean;
  isRequired?: boolean;
  countLabel?: string;
  secondaryLabel?: string;
}
const SectionCard: React.FC<SectionCardProps> = ({
  id,
  title,
  count,
  lastUpdated,
  icon: Icon,
  onClick,
  ctaLabel = 'View All',
  emptyCtaLabel = 'Add First',
  isConfigured,
  highlight = false,
  isRequired = false,
  countLabel = '',
  secondaryLabel,
}) => {
  const showBadge = typeof count === 'number' && count > 0;
  return (
    // `@container` turns each card into a size-queryable container. The
    // AccordionTrigger's chevron uses a `@max-[15rem]:hidden` rule (see
    // accordion.tsx) so it disappears when the card itself is squeezed
    // below ~240px — buying back ~32px of row width for title + badge.
    <AccordionItem value={id} className="group @container border-none">
      <Card className={`p-0 overflow-hidden hover:shadow-none transition-shadow duration-200 ${
        highlight
          ? 'border-primary bg-primary/5'
          : isRequired && !isConfigured
            ? 'border-amber-300 bg-amber-50/30 dark:bg-amber-950/20'
            : ''
      }`}>
        {/* Header row: icon, title, badge, chevron are all direct siblings of
            the trigger — no wrapper div. Layout guarantees:
              • chevron is always pinned to the right (trigger `px-4 sm:px-5`
                + `justify-between` + chevron `shrink-0`);
              • badge sits at a fixed offset just left of the chevron
                (`shrink-0` + gap-3);
              • title fills whatever's left and truncates with ellipsis when
                needed (`flex-1 min-w-0 truncate`). */}
        <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
          <Icon className="w-6 h-6 shrink-0 text-primary" />
          <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
            {title}
          </h3>
          {showBadge && (
            <Badge
              variant="secondary"
              className="shrink-0 group-data-[state=open]:hidden"
            >
              {count}
            </Badge>
          )}
        </AccordionTrigger>
        <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
          <div className="mb-1">
            <span className="text-xl sm:text-2xl font-bold text-primary">
              {count}
            </span>
            {countLabel && (
              <span className="text-sm text-muted-foreground ml-1">{countLabel}</span>
            )}
            {typeof count === 'number' && !countLabel && (
              <span className="text-sm text-muted-foreground ml-1">
                {count === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          {secondaryLabel && (
            <p className="text-xs text-muted-foreground mb-1">{secondaryLabel}</p>
          )}
          <p className="text-xs text-muted-foreground mb-4">
            {lastUpdated ? `Last added: ${formatTimeAgo(lastUpdated)}` : 'No updates yet'}
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="hover:bg-primary hover:text-primary-foreground"
            onClick={onClick}
          >
            <Plus className="w-4 h-4 mr-2" />
            {count === 0 ? emptyCtaLabel : ctaLabel}
          </Button>
        </AccordionContent>
      </Card>
    </AccordionItem>
  );
};

const Dashboard: React.FC<DashboardProps> = ({ domain, user, setTab: _setTab, unopenedLeadsCount, refreshKey }) => {
  const { hasContactLeadsAccess, hasVisitorAnalyticsAccess, openUpgradeModal, planInfo, isPro, isProMax } = usePlan();
  // When the live-preview panel is open it takes ~50% of the viewport, so
  // squeezing 3 cards into a row would leave each ~200px wide. Drop to 2
  // columns in that case — each card gets ~300px and the tinted "Configured"
  // / count badges have room to breathe alongside the title.
  const { isPreviewOpen } = usePreview();
  const cardGridClass = isPreviewOpen
    ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';
  const navigate = useNavigate();
  const setTab = (tab: string) => navigate(getPathForTab(tab));
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    heroSection: { count: 0, lastUpdated: null, isActive: false, hasData: false },
    aboutSection: { count: 0, lastUpdated: null, isActive: false, hasData: false },
    showcase: { count: 0, lastUpdated: null },
    imageGallery: { count: 0, lastUpdated: null },
    websiteLeads: { count: 0, lastUpdated: null },
    promoBanner: { count: 0, lastUpdated: null },
    toursEvents: { count: 0, lastUpdated: null },
    awards: { count: 0, lastUpdated: null },
    categories: { count: 0, lastUpdated: null },
    infoKit: { count: 0, lastUpdated: null, downloadCount: 0 },
    platforms: { count: 0, lastUpdated: null },
    services: { count: 0, lastUpdated: null },
    artistRoster: { count: 0, lastUpdated: null },
    testimonials: { count: 0, lastUpdated: null },
    team: { count: 0, lastUpdated: null },
    clients: { count: 0, lastUpdated: null },
    siteType: 'artist',
    toursEventsActive: true,
    awardsActive: true,
  });
  const [profileCompleteness, setProfileCompleteness] = useState<ProfileCompleteness>({
    percentage: 0,
    missingItems: [],
    suggestions: [],
  });
  const [activityFeed, setActivityFeed] = useState<ActivityItem[]>([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visitorTrends, setVisitorTrends] = useState<VisitorTrends | null>(null);
  const [trendsDays, setTrendsDays] = useState<7 | 30 | 'all' | 'today' | 'range'>(30);
  const [trendsFrom, setTrendsFrom] = useState<string>('');
  const [trendsTo, setTrendsTo] = useState<string>('');
  const [trendsLoading, setTrendsLoading] = useState(false);

  // Which accordion cards are currently expanded. Default is EMPTY (all
  // collapsed) — the whole point of the accordion pattern is to save vertical
  // space on first load. The collapsed header still carries useful signal
  // (icon, title, status/count badge), so users can scan the dashboard and
  // expand only the sections they care about. Choices persist per-browser.
  const [expandedCards, setExpandedCards] = useState<string[]>(
    () => readStoredExpanded([])
  );

  const handleExpandedChange = (next: string[]) => {
    setExpandedCards(next);
    try {
      window.localStorage.setItem(DASHBOARD_EXPANDED_KEY, JSON.stringify(next));
    } catch { /* localStorage unavailable — in-memory only */ }
  };

  useEffect(() => {
    if (domain) {
      fetchDashboardData();
    }
  }, [domain, refreshKey]);

  useEffect(() => {
    if (domain && hasVisitorAnalyticsAccess) {
      fetchVisitorTrends();
    }
  }, [domain, hasVisitorAnalyticsAccess, trendsDays, trendsFrom, trendsTo, refreshKey]);

  const fetchVisitorTrends = async () => {
    setTrendsLoading(true);
    try {
      const params: Record<string, any> = {
        days: trendsDays === 'all' || trendsDays === 'today' || trendsDays === 'range' ? trendsDays : trendsDays,
      };
      if (trendsDays === 'range') {
        if (trendsFrom) params.from = trendsFrom;
        if (trendsTo) params.to = trendsTo;
      }
      const res = await axios.get(`${BASE_URL}/api/admin/domain-visitor-trends`, { params });
      setVisitorTrends(res.data);
    } catch (error) {
      console.error('Error fetching visitor trends:', error);
    } finally {
      setTrendsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Single aggregated API call replaces 12 separate calls
      const { data } = await axios.get(`${BASE_URL}/api/admin/dashboard-summary`);

      const { sections, sectionConfig, heroHasImage, aboutHasImage, platformsCount: platCount, recentActivity } = data;
      const toursEventsActive = sectionConfig.toursEventsActive;
      const awardsActive = sectionConfig.awardsActive;

      setDashboardData({
        heroSection: sections.heroSection,
        aboutSection: sections.aboutSection,
        showcase: sections.showcase,
        imageGallery: sections.imageGallery,
        websiteLeads: sections.websiteLeads,
        promoBanner: sections.promoBanner,
        toursEvents: sections.toursEvents,
        awards: sections.awards,
        categories: sections.categories,
        infoKit: { downloadCount: 0, ...(sections.infoKit || {}) },
        platforms: sections.platforms,
        services: { count: 0, lastUpdated: null, ...(sections.services || {}) },
        artistRoster: { count: 0, lastUpdated: null, ...(sections.artistRoster || {}) },
        testimonials: { count: 0, lastUpdated: null, ...(sections.testimonials || {}) },
        team: { count: 0, lastUpdated: null, ...(sections.team || {}) },
        clients: { count: 0, lastUpdated: null, ...(sections.clients || {}) },
        siteType: data.siteType === 'management' ? 'management' : 'artist',
        toursEventsActive,
        awardsActive,
      });

      // Calculate profile completeness
      const missingItems: { label: string; tab: string }[] = [];
      const suggestions: string[] = [];
      let completedItems = 0;
      let totalItems = 8;
      if (toursEventsActive) totalItems++;
      if (awardsActive) totalItems++;

      if (sections.heroSection.hasData) completedItems++; else missingItems.push({ label: 'Hero Section', tab: 'heroSection' });
      if (sections.aboutSection.hasData) completedItems++; else missingItems.push({ label: 'About Section', tab: 'aboutSection' });
      if (sections.showcase.count > 0) completedItems++; else missingItems.push({ label: 'Showcase (Music)', tab: 'music' });
      if (sections.imageGallery.count > 0) completedItems++; else missingItems.push({ label: 'Image Gallery', tab: 'image' });
      if (sections.promoBanner.count > 0) completedItems++; else missingItems.push({ label: 'Promo Banner', tab: 'banner' });
      if (toursEventsActive) {
        if (sections.toursEvents.count > 0) completedItems++; else missingItems.push({ label: 'Tours & Events', tab: 'toursEvents' });
      }
      if (awardsActive) {
        if (sections.awards.count > 0) completedItems++; else missingItems.push({ label: 'Awards', tab: 'awardsSection' });
      }
      if (sections.categories.count > 0) completedItems++; else missingItems.push({ label: 'Categories', tab: 'category' });
      if (platCount >= 3) completedItems++; else missingItems.push({ label: 'Social Platforms (3+)', tab: 'platforms' });
      if (heroHasImage && aboutHasImage) completedItems++; else if (!missingItems.find(m => m.label.includes('Hero') || m.label.includes('About'))) missingItems.push({ label: 'Profile Images', tab: 'heroSection' });

      if (!sections.heroSection.hasData) suggestions.push('Add a compelling hero section to make a strong first impression');
      if (sections.showcase.count < 3) suggestions.push('Add more music tracks to showcase your portfolio');
      if (platCount < 3) suggestions.push('Connect more social platforms to increase your reach');
      if (sections.imageGallery.count < 5) suggestions.push('Upload more images to create a rich gallery experience');
      if (toursEventsActive && sections.toursEvents.count === 0) suggestions.push('Add upcoming events to keep fans engaged');

      setProfileCompleteness({
        percentage: Math.round((completedItems / totalItems) * 100),
        missingItems: missingItems.slice(0, 4),
        suggestions: suggestions.slice(0, 3),
      });

      // Build activity feed from server-provided recent items
      const activities: ActivityItem[] = [];
      const serverActivityFeed = Array.isArray(recentActivity?.activityFeed)
        ? recentActivity.activityFeed
        : [];

      if (serverActivityFeed.length > 0) {
        const validTypes = new Set(['lead', 'event', 'content', 'section', 'settings']);
        activities.push(...serverActivityFeed.map((activity: any) => ({
          _id: activity._id,
          type: validTypes.has(activity.type) ? activity.type : 'content',
          title: activity.title || 'Activity',
          description: activity.description || '',
          timestamp: activity.timestamp,
          icon: activity.icon || 'activity',
          actor: activity.actor,
        })));
      }
      
      (recentActivity.leads || []).forEach((lead: any) => {
        activities.push({ _id: lead._id, type: 'lead', title: 'New website inquiry', description: `${lead.fullName} - ${lead.purposeOfContact}`, timestamp: lead.submittedAt || lead.createdAt, icon: 'mail', actor: { name: 'Website Visitor', role: 'Lead' } });
      });

      if (serverActivityFeed.length > 0) {
        activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setActivityFeed(activities.slice(0, 30));
        return;
      }

      (recentActivity.events || []).forEach((event: any) => {
        activities.push({ _id: event._id, type: 'event', title: event.eventName, description: `${event.venue}${event.city ? `, ${event.city}` : ''}`, timestamp: event.updatedAt || event.createdAt, icon: 'calendar', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.showcase || []).forEach((item: any) => {
        activities.push({ _id: item._id, type: 'content', title: item.title || 'Showcase Video', description: 'Video added to showcase', timestamp: item.updatedAt || item.createdAt, icon: 'music', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.images || []).forEach((image: any) => {
        activities.push({ _id: image._id, type: 'content', title: image.title || image.alt || 'Gallery Image', description: 'Image added to gallery', timestamp: image.updatedAt || image.createdAt, icon: 'image', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.banners || []).forEach((banner: any) => {
        activities.push({ _id: banner._id, type: 'content', title: banner.title || 'Promo Banner', description: 'Banner updated', timestamp: banner.updatedAt || banner.createdAt, icon: 'megaphone', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.awards || []).forEach((award: any) => {
        activities.push({ _id: award._id, type: 'content', title: award.title || award.awardName || 'Award', description: 'Award added', timestamp: award.updatedAt || award.createdAt, icon: 'award', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.categories || []).forEach((category: any) => {
        activities.push({ _id: category._id, type: 'settings', title: category.name || 'Category', description: 'Category added', timestamp: category.updatedAt || category.createdAt, icon: 'tag', actor: { name: 'Unknown', role: 'Legacy data' } });
      });
      (recentActivity.platforms || []).forEach((platform: any) => {
        activities.push({ _id: platform._id, type: 'settings', title: platform.platform_name || platform.name || 'Platform', description: 'Social platform linked', timestamp: platform.updatedAt || platform.createdAt, icon: 'share', actor: { name: 'Unknown', role: 'Legacy data' } });
      });

      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setActivityFeed(activities.slice(0, 30));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };


  // Activity Icon Component
  const getActivityIcon = (icon: string) => {
    switch (icon) {
      case 'mail': return <Mail className="w-4 h-4" />;
      case 'calendar': return <Calendar className="w-4 h-4" />;
      case 'music': return <Music className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'layout': return <Layout className="w-4 h-4" />;
      case 'user': return <User className="w-4 h-4" />;
      case 'award': return <Award className="w-4 h-4" />;
      case 'tag': return <Tag className="w-4 h-4" />;
      case 'share': return <Share2 className="w-4 h-4" />;
      case 'megaphone': return <Megaphone className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const formatActivityActorMeta = (activity: ActivityItem) => {
    const role = activity.actor?.role || '';
    const organization = activity.actor?.organizationName || '';
    return [role, organization].filter(Boolean).join(' - ');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-64 text-muted-foreground">
        <Spinner size="xl" />
        <p className="text-sm">Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 animate-in slide-in-from-top duration-500">
        <div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
              {profileCompleteness.percentage === 0 
                ? `Let's launch your website, ${user?.fullName || user?.username}! 🚀`
                : profileCompleteness.percentage < 50
                  ? `Good start, ${user?.fullName || user?.username}! Keep going 💪`
                  : `Welcome back, ${user?.fullName || user?.username}! 👋`}
            </h1>
            {/* Plan Status Badge */}
            {planInfo && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                isProMax
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : isPro
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : planInfo.isInGracePeriod
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {isProMax ? (
                  <>
                    <Crown className="w-3 h-3" />
                    Pro Max
                  </>
                ) : isPro ? (
                  <>
                    <Crown className="w-3 h-3" />
                    Pro
                  </>
                ) : planInfo.isInGracePeriod ? (
                  <>
                    <Crown className="w-3 h-3" />
                    Grace Period ({planInfo.gracePeriodDaysRemaining} days left)
                  </>
                ) : (
                  'Unpaid'
                )}
              </span>
            )}
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {profileCompleteness.percentage < 50
              ? "Complete the steps below to make your website live"
              : "Here's an overview of your website"}
          </p>
        </div>
        <div className="hidden sm:block text-right shrink-0">
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>

      {domain && (
        <>

{/* Visitor Analytics - Full Width (Paid feature) */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-[350ms]">
            {!hasVisitorAnalyticsAccess ? (
              <Card className="p-4 sm:p-6 bg-primary-light hover:bg-primary-light relative overflow-hidden">
                <div className="absolute top-5 right-5">
                  <Crown className="w-7 h-7 text-primary" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-0 rounded-xl text-primary">
                    <Lock className="w-7 h-7" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground">Visitor Analytics</h3>
                </div>
                <p className="text-sm text-muted-foreground max-w-xl mb-4">
                  Track daily visitor trends, sessions, and unique visitors for your website.
                  Upgrade to Pro to unlock detailed analytics for your domain.
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="text-white"
                  onClick={() => openUpgradeModal('visitorAnalyticsAccess')}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
              </Card>
            ) : (
              <Card className="p-4 sm:p-6 hover:shadow-none transition-all duration-300">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-primary flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Visitor Analytics
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant={trendsDays === 'today' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsDays('today')}
                    >
                      Today
                    </Button>
                    <Button
                      variant={trendsDays === 7 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsDays(7)}
                    >
                      7 Days
                    </Button>
                    <Button
                      variant={trendsDays === 30 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsDays(30)}
                    >
                      30 Days
                    </Button>
                    <Button
                      variant={trendsDays === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsDays('all')}
                    >
                      All Time
                    </Button>
                    <Button
                      variant={trendsDays === 'range' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsDays('range')}
                    >
                      <Calendar className="w-4 h-4 mr-1.5" />
                      Date Range
                    </Button>
                  </div>
                </div>

                {trendsDays === 'range' && (
                  <div className="mb-4 flex justify-end">
                    <div className="inline-block p-4 rounded-xl border border-border bg-muted/30 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="flex flex-wrap items-end justify-end gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-medium text-muted-foreground">From</label>
                          <input
                            type="date"
                            value={trendsFrom}
                            max={trendsTo || undefined}
                            onChange={(e) => setTrendsFrom(e.target.value)}
                            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-medium text-muted-foreground">To</label>
                          <input
                            type="date"
                            value={trendsTo}
                            min={trendsFrom || undefined}
                            onChange={(e) => setTrendsTo(e.target.value)}
                            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        </div>
                        {(trendsFrom || trendsTo) && (
                          <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground invisible">Clear</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setTrendsFrom(''); setTrendsTo(''); }}
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                      </div>
                      {trendsFrom && trendsTo && (
                        <p className="mt-3 text-xs text-right text-muted-foreground">
                          Showing data from{' '}
                          <span className="font-medium text-foreground">
                            {new Date(trendsFrom + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium text-foreground">
                            {new Date(trendsTo + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Comparison Stats */}
                {visitorTrends?.comparison && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground">Total Visits</p>
                      <p className="text-lg font-bold">{visitorTrends.comparison.sessions.current}</p>
                      {visitorTrends.comparison.sessions.change !== null ? (
                        <div className={`flex items-center gap-1 text-xs ${visitorTrends.comparison.sessions.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          <TrendingUp className={`w-3 h-3 ${visitorTrends.comparison.sessions.change < 0 && 'rotate-180'}`} />
                          <span>{Math.abs(visitorTrends.comparison.sessions.change)}%</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Since start</p>
                      )}
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground">Unique Visitors</p>
                      <p className="text-lg font-bold">{visitorTrends.comparison.uniqueVisitors.current}</p>
                      {visitorTrends.comparison.uniqueVisitors.change !== null ? (
                        <div className={`flex items-center gap-1 text-xs ${visitorTrends.comparison.uniqueVisitors.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          <TrendingUp className={`w-3 h-3 ${visitorTrends.comparison.uniqueVisitors.change < 0 && 'rotate-180'}`} />
                          <span>{Math.abs(visitorTrends.comparison.uniqueVisitors.change)}%</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Since start</p>
                      )}
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground">Avg. Duration</p>
                      <p className="text-lg font-bold">
                        {visitorTrends.daily.length > 0
                          ? Math.round(visitorTrends.daily.reduce((s, d) => s + d.avgDurationSeconds, 0) / visitorTrends.daily.filter(d => d.avgDurationSeconds > 0).length || 1)
                          : 0}s
                      </p>
                      <p className="text-xs text-muted-foreground">per session</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground">New vs Returning</p>
                      <p className="text-lg font-bold">
                        {visitorTrends.daily.reduce((s, d) => s + d.newVisitors, 0)} / {visitorTrends.daily.reduce((s, d) => s + d.returningVisitors, 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">new / returning</p>
                    </div>
                  </div>
                )}

                {/* Chart */}
                {trendsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Spinner size="lg" />
                  </div>
                ) : visitorTrends && visitorTrends.daily.length > 0 ? (
                  <ChartContainer
                    config={visitorTrendsChartConfig}
                    className="h-72 w-full"
                  >
                    <AreaChart data={visitorTrends.daily} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="fillSessions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="fillUnique" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={(v: string) => {
                          const d = new Date(v + 'T00:00:00');
                          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        allowDecimals={false}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            labelFormatter={(label: any) => {
                              const d = new Date(String(label) + 'T00:00:00');
                              return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                            }}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Area
                        type="monotone"
                        dataKey="sessions"
                        stroke="var(--color-primary-light2)"
                        fill="url(#fillSessions)"
                        strokeWidth={2}
                        stackId="a"
                      />
                      <Area
                        type="monotone"
                        dataKey="uniqueVisitors"
                        stroke="var(--color-success)"
                        fill="url(#fillUnique)"
                        strokeWidth={2}
                        stackId="b"
                      />
                    </AreaChart>
                  </ChartContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
                    <div className="text-center">
                      <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      No visitor data available for this period
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Row 1: Hero Section, About Section & Enquiries */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Main Sections</h2>
            </div>
            <Accordion
              type="multiple"
              value={expandedCards}
              onValueChange={handleExpandedChange}
              className={cardGridClass}
            >
              {/* Hero Section — bespoke content (Configured vs Not Set Up),
                  wrapped in the same accordion pattern. Collapsed header
                  shows a green "Configured" / amber "Not Set Up" badge. */}
              <AccordionItem value="heroSection" className="group @container border-none">
                <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    <Layout className="w-6 h-6 shrink-0 text-primary" />
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      Hero Section
                    </h3>
                    <Badge
                      variant="secondary"
                      className={`shrink-0 group-data-[state=open]:hidden ${
                        dashboardData.heroSection.hasData
                          ? 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400'
                          : 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}
                    >
                      {dashboardData.heroSection.hasData ? 'Configured' : 'Not Set Up'}
                    </Badge>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <div className="mb-1">
                      <span className={`text-base sm:text-lg font-semibold ${
                        dashboardData.heroSection.hasData ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {dashboardData.heroSection.hasData ? 'Configured' : 'Not Set Up'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                      {dashboardData.heroSection.lastUpdated
                        ? `Last updated: ${formatTimeAgo(dashboardData.heroSection.lastUpdated)}`
                        : 'No updates yet'}
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="hover:bg-primary hover:text-primary-foreground"
                      onClick={() => setTab('heroSection')}
                    >
                      {dashboardData.heroSection.hasData
                        ? <Pencil className="w-4 h-4 mr-2" />
                        : <Plus className="w-4 h-4 mr-2" />}
                      {dashboardData.heroSection.hasData ? 'Edit' : 'Set Up'}
                    </Button>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* About Section — same shape as Hero. */}
              <AccordionItem value="aboutSection" className="group @container border-none">
                <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    <User className="w-6 h-6 shrink-0 text-primary" />
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      About Section
                    </h3>
                    <Badge
                      variant="secondary"
                      className={`shrink-0 group-data-[state=open]:hidden ${
                        dashboardData.aboutSection.hasData
                          ? 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400'
                          : 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}
                    >
                      {dashboardData.aboutSection.hasData ? 'Configured' : 'Not Set Up'}
                    </Badge>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <div className="mb-1">
                      <span className={`text-base sm:text-lg font-semibold ${
                        dashboardData.aboutSection.hasData ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {dashboardData.aboutSection.hasData ? 'Configured' : 'Not Set Up'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                      {dashboardData.aboutSection.lastUpdated
                        ? `Last updated: ${formatTimeAgo(dashboardData.aboutSection.lastUpdated)}`
                        : 'No updates yet'}
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="hover:bg-primary hover:text-primary-foreground"
                      onClick={() => setTab('aboutSection')}
                    >
                      {dashboardData.aboutSection.hasData
                        ? <Pencil className="w-4 h-4 mr-2" />
                        : <Plus className="w-4 h-4 mr-2" />}
                      {dashboardData.aboutSection.hasData ? 'Edit' : 'Set Up'}
                    </Button>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Profile Status — percentage + progress bar + missing/suggestions
                  lists. Collapsed header surfaces the percentage as a badge. */}
              <AccordionItem value="profileStatus" className="group @container border-none">
                <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    <CheckCircle className="w-6 h-6 shrink-0 text-primary" />
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      Profile Status
                    </h3>
                    <Badge
                      variant="secondary"
                      className={`shrink-0 group-data-[state=open]:hidden ${
                        profileCompleteness.percentage >= 80
                          ? 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400'
                          : profileCompleteness.percentage >= 50
                            ? 'text-primary bg-primary/10'
                            : 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}
                    >
                      {profileCompleteness.percentage}%
                    </Badge>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <div className="mb-2">
                      <span className={`text-xl sm:text-2xl font-bold ${
                        profileCompleteness.percentage >= 80 ? 'text-green-600' :
                        profileCompleteness.percentage >= 50 ? 'text-primary' : 'text-amber-600'
                      }`}>
                        {profileCompleteness.percentage}%
                      </span>
                      <span className="text-sm text-muted-foreground ml-2">Complete</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-4">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${
                          profileCompleteness.percentage >= 80 ? 'bg-green-500' :
                          profileCompleteness.percentage >= 50 ? 'bg-primary' : 'bg-amber-500'
                        }`}
                        style={{ width: `${profileCompleteness.percentage}%` }}
                      />
                    </div>

                    {profileCompleteness.missingItems.length > 0 && profileCompleteness.percentage < 100 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-foreground mb-2">Missing Info:</p>
                        <div className="space-y-1">
                          {profileCompleteness.missingItems.slice(0, 2).map((item, index) => (
                            <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                              <span>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {profileCompleteness.suggestions.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-foreground mb-2">Suggestions:</p>
                        <div className="space-y-1">
                          {profileCompleteness.suggestions.slice(0, 2).map((suggestion, index) => (
                            <div key={index} className="flex items-start gap-2 text-xs text-muted-foreground">
                              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                              <span className="line-clamp-2">{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Theme — deep-links to the "Theme" inner tab of Website
                  Settings via `location.state.brandingTab`, which
                  SiteBrandingManager reads on mount to pre-select the tab. */}
              <AccordionItem value="theme" className="group @container border-none">
                <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    <Palette className="w-6 h-6 shrink-0 text-primary" />
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      Theme
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <p className="text-xs text-muted-foreground mb-4">
                      Choose or customize your website's theme, colors, and fonts.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="hover:bg-primary hover:text-primary-foreground"
                      onClick={() => navigate(getPathForTab('siteBranding'), { state: { brandingTab: 'theme' } })}
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Customize Theme
                    </Button>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Custom Domain — deep-links to the "Custom Domain" inner tab.
                  Card is a lightweight entry point; the target settings page
                  handles the paid-feature gating and DNS verification flow. */}
              <AccordionItem value="customDomain" className="group @container border-none">
                <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    <Globe className="w-6 h-6 shrink-0 text-primary" />
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      Custom Domain
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <p className="text-xs text-muted-foreground mb-4">
                      Connect your own domain (e.g. yourname.com) to your website.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="hover:bg-primary hover:text-primary-foreground"
                      onClick={() => navigate(getPathForTab('siteBranding'), { state: { brandingTab: 'customDomain' } })}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Manage Domain
                    </Button>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Row 2: Showcase, Image Gallery, Profile Status */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Content & Engagement</h2>
            </div>
            <Accordion
              type="multiple"
              value={expandedCards}
              onValueChange={handleExpandedChange}
              className={cardGridClass}
            >
              <SectionCard 
                id="showcase"
                title="Showcase" 
                count={dashboardData.showcase.count} 
                lastUpdated={dashboardData.showcase.lastUpdated}
                icon={Music} 
                onClick={() => navigate(getPathForTab('music'), { state: { openAdd: true } })}
                ctaLabel="Add Showcase"
                emptyCtaLabel="Add Showcase"
                countLabel="Showcase Items"
              />
              <SectionCard 
                id="imageGallery"
                title="Image Gallery" 
                count={dashboardData.imageGallery.count} 
                lastUpdated={dashboardData.imageGallery.lastUpdated}
                icon={Image} 
                onClick={() => navigate(getPathForTab('image'), { state: { openAdd: true } })}
                ctaLabel="Add Image"
                emptyCtaLabel="Add Image"
                countLabel="Images"
              />

              {/* Management-only content (siteType 'management' websites) */}
              {dashboardData.siteType === 'management' && (
                <>
                  <SectionCard
                    id="services"
                    title="Services"
                    count={dashboardData.services.count}
                    lastUpdated={dashboardData.services.lastUpdated}
                    icon={BriefcaseBusiness}
                    onClick={() => navigate(getPathForTab('services'), { state: { openAdd: true } })}
                    ctaLabel="Add Service"
                    emptyCtaLabel="Add Service"
                    countLabel="Services"
                  />
                  <SectionCard
                    id="artistRoster"
                    title="Artist Roster"
                    count={dashboardData.artistRoster.count}
                    lastUpdated={dashboardData.artistRoster.lastUpdated}
                    icon={Users}
                    onClick={() => navigate(getPathForTab('artistRoster'), { state: { openAdd: true } })}
                    ctaLabel="Add Artist"
                    emptyCtaLabel="Add Artist"
                    countLabel="Artists"
                  />
                  <SectionCard
                    id="testimonials"
                    title="Testimonials"
                    count={dashboardData.testimonials.count}
                    lastUpdated={dashboardData.testimonials.lastUpdated}
                    icon={Quote}
                    onClick={() => navigate(getPathForTab('testimonials'), { state: { openAdd: true } })}
                    ctaLabel="Add Testimonial"
                    emptyCtaLabel="Add Testimonial"
                    countLabel="Testimonials"
                  />
                  <SectionCard
                    id="team"
                    title="Team"
                    count={dashboardData.team.count}
                    lastUpdated={dashboardData.team.lastUpdated}
                    icon={UserCog}
                    onClick={() => navigate(getPathForTab('team'), { state: { openAdd: true } })}
                    ctaLabel="Add Member"
                    emptyCtaLabel="Add Member"
                    countLabel="Team Members"
                  />
                  <SectionCard
                    id="clients"
                    title="Clients"
                    count={dashboardData.clients.count}
                    lastUpdated={dashboardData.clients.lastUpdated}
                    icon={Building2}
                    onClick={() => navigate(getPathForTab('clients'), { state: { openAdd: true } })}
                    ctaLabel="Add Client"
                    emptyCtaLabel="Add Client"
                    countLabel="Clients"
                  />
                </>
              )}

              {/* Website Leads — bespoke content (locked/unlocked branches),
                  wrapped in the same accordion pattern as SectionCard. */}
              <AccordionItem value="websiteLeads" className="group @container border-none">
                <Card className={`p-0 overflow-hidden hover:shadow-none transition-shadow duration-200 ${
                  !hasContactLeadsAccess
                    ? 'bg-primary-light'
                    : unopenedLeadsCount > 0 ? 'border-primary bg-primary/5' : ''
                }`}>
                  <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
                    {!hasContactLeadsAccess
                      ? <Lock className="w-6 h-6 shrink-0 text-primary" />
                      : <Mail className="w-6 h-6 shrink-0 text-primary" />}
                    <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
                      Website Leads
                    </h3>
                    {!hasContactLeadsAccess ? (
                      <Crown className="w-5 h-5 shrink-0 text-primary group-data-[state=open]:hidden" aria-label="Upgrade required" />
                    ) : (unopenedLeadsCount > 0 || dashboardData.websiteLeads.count > 0) && (
                      <Badge variant="secondary" className="shrink-0 group-data-[state=open]:hidden">
                        {unopenedLeadsCount > 0 ? unopenedLeadsCount : dashboardData.websiteLeads.count}
                      </Badge>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
                    <div className="mb-1">
                      {!hasContactLeadsAccess ? (
                        <span className="text-sm text-primary font-semibold">Upgrade to unlock</span>
                      ) : (
                        <>
                          <span className="text-xl sm:text-2xl font-bold text-primary">
                            {unopenedLeadsCount > 0 ? unopenedLeadsCount : dashboardData.websiteLeads.count}
                          </span>
                          <span className="text-sm text-muted-foreground ml-1">
                            {unopenedLeadsCount > 0 ? 'New Leads' : 'Total Leads'}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                      {!hasContactLeadsAccess
                        ? 'View all contact form submissions'
                        : dashboardData.websiteLeads.count === 0
                          ? 'No leads yet'
                          : `${dashboardData.websiteLeads.count} total received`
                      }
                    </p>
                    <Button
                      variant={!hasContactLeadsAccess ? 'default' : 'secondary'}
                      size="sm"
                      className={!hasContactLeadsAccess ? ' text-white' : 'hover:bg-primary hover:text-primary-foreground'}
                      onClick={() => {
                        if (!hasContactLeadsAccess) {
                          openUpgradeModal('contactLeadsAccess');
                        } else {
                          setTab('contactWebsite');
                        }
                      }}
                    >
                      {!hasContactLeadsAccess ? (
                        <>
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          View Leads
                        </>
                      )}
                    </Button>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Row 3: Promo Banner, Tours & Events, Awards, Categories, Platforms */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Additional Sections</h2>
            </div>
            <Accordion
              type="multiple"
              value={expandedCards}
              onValueChange={handleExpandedChange}
              className={cardGridClass}
            >
              <SectionCard 
                id="promoBanner"
                title="Promo Banner" 
                count={dashboardData.promoBanner.count} 
                lastUpdated={dashboardData.promoBanner.lastUpdated}
                icon={Megaphone} 
                onClick={() => navigate(getPathForTab('banner'), { state: { openAdd: true } })}
                ctaLabel="Add Banner"
                emptyCtaLabel="Add Banner"
                countLabel="Banners"
              />
              {dashboardData.toursEventsActive && (
                <SectionCard 
                  id="toursEvents"
                  title="Tours & Events" 
                  count={dashboardData.toursEvents.count} 
                  lastUpdated={dashboardData.toursEvents.lastUpdated}
                  icon={Calendar} 
                  onClick={() => navigate(getPathForTab('toursEvents'), { state: { openAdd: true } })}
                  ctaLabel="Add Event"
                  emptyCtaLabel="Add Event"
                  countLabel="Events"
                />
              )}
              {dashboardData.awardsActive && (
                <SectionCard 
                  id="awards"
                  title="Awards" 
                  count={dashboardData.awards.count} 
                  lastUpdated={dashboardData.awards.lastUpdated}
                  icon={Award} 
                  onClick={() => navigate(getPathForTab('awardsSection'), { state: { openAdd: true } })}
                  ctaLabel="Add Award"
                  emptyCtaLabel="Add Award"
                  countLabel="Awards"
                />
              )}
              <SectionCard 
                id="categories"
                title="Categories" 
                count={dashboardData.categories.count} 
                lastUpdated={dashboardData.categories.lastUpdated}
                icon={Tag} 
                onClick={() => setTab('category')}
                ctaLabel="Add Category"
                emptyCtaLabel="Add Category"
                countLabel="Categories"
              />
              <SectionCard 
                id="infoKit"
                title="Info Kit" 
                count={dashboardData.infoKit.downloadCount || 0} 
                lastUpdated={dashboardData.infoKit.lastUpdated}
                icon={FileArchive} 
                onClick={() => setTab('infoKit')}
                ctaLabel="Upload ZIP"
                emptyCtaLabel="Upload ZIP"
                countLabel="Downloads"
              />
              <SectionCard 
                id="platforms"
                title="Platforms" 
                count={dashboardData.platforms.count} 
                lastUpdated={dashboardData.platforms.lastUpdated}
                icon={Share2} 
                onClick={() => setTab('platforms')}
                ctaLabel="Add Platform"
                emptyCtaLabel="Add Platform"
                countLabel="Platforms"
              />
            </Accordion>
          </div>

          

          {/* Activity Feed - Full Width */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-[400ms]">
            <Card className="p-4 sm:p-6 hover:shadow-none transition-all duration-300">
              <h3 className="text-base sm:text-lg font-semibold text-primary mb-3 sm:mb-4">Activity Feed</h3>
              
              {activityFeed.length > 0 ? (
                <>
                  <div className="overflow-hidden rounded-lg">
                    <div className="hidden sm:grid grid-cols-[2.5rem_minmax(0,3fr)_minmax(0,2fr)_auto] items-center gap-4 border-b border-border bg-muted/20 px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span aria-hidden="true" />
                      <span>Activity</span>
                      <span>By</span>
                      <span className="text-right">When</span>
                    </div>
                    {(showAllActivity ? activityFeed : activityFeed.slice(0, 8)).map((activity) => (
                      <div 
                        key={activity._id}
                        className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] sm:grid-cols-[2.5rem_minmax(0,3fr)_minmax(0,2fr)_auto] items-center gap-3 sm:gap-4 border-b border-border/70 px-3 py-4 last:border-b-0 hover:bg-muted/30 transition-colors"
                      >
                      <div className={`shrink-0 ${
                        activity.type === 'lead' ? 'text-blue-500' :
                        activity.type === 'event' ? 'text-purple-500' :
                        activity.type === 'section' ? 'text-amber-500' :
                        activity.type === 'settings' ? 'text-slate-500' :
                        'text-green-500'
                      } flex justify-center`}>
                        {getActivityIcon(activity.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate sm:whitespace-normal">
                          <span className="font-medium">{activity.title}:</span>{' '}
                          <span className="text-muted-foreground">{activity.description}</span>
                        </p>
                        <p className="sm:hidden text-xs text-muted-foreground truncate mt-0.5">
                          By <span className="text-foreground">{activity.actor?.name || 'Unknown'}</span>
                          {formatActivityActorMeta(activity) ? ` - ${formatActivityActorMeta(activity)}` : ''}
                        </p>
                      </div>
                      <div className="hidden sm:block min-w-0">
                        <p className="text-sm truncate">{activity.actor?.name || 'Unknown'}</p>
                        {formatActivityActorMeta(activity) && (
                          <p className="text-xs text-muted-foreground truncate">
                            {formatActivityActorMeta(activity)}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground whitespace-nowrap shrink-0">
                        {formatActivityTimestamp(activity.timestamp)}
                      </div>
                      </div>
                    ))}
                  </div>
                  
                  {activityFeed.length > 8 && (
                    <div className="flex justify-center pt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setShowAllActivity(prev => !prev)}
                      >
                        {showAllActivity ? 'Show less' : `Show all (${activityFeed.length})`}
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-4 text-xs">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span className="text-muted-foreground">Your last update was</span>
                    <span className="text-primary font-medium">
                      {activityFeed.length > 0 ? formatTimeAgo(activityFeed[0].timestamp) : 'never'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No recent activity</p>
                  <p className="text-xs">Start adding content to see activity here</p>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
